import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Save } from "lucide-react";
import {
  drugGenericService,
  CreateDrugGenericData,
} from "../../services/drug-generic.service";

const SCHEDULE_TYPES = [
  { value: "GENERAL", label: "General" },
  { value: "SCHEDULE_G", label: "Schedule G" },
  { value: "SCHEDULE_H", label: "Schedule H" },
  { value: "SCHEDULE_H1", label: "Schedule H1" },
  { value: "SCHEDULE_X", label: "Schedule X" },
];

export default function EditDrugGenericPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState<Partial<CreateDrugGenericData>>({
    name: "",
    code: "",
    description: "",
    scheduleType: "",
  });

  const { data: generic, isLoading } = useQuery({
    queryKey: ["drug-generic", id],
    queryFn: () => drugGenericService.getById(id!),
    enabled: !!id,
  });

  useEffect(() => {
    if (generic) {
      setFormData({
        name: generic.name,
        code: generic.code,
        description: generic.description,
        scheduleType: generic.scheduleType,
        formId: generic.form?.id,
        therapeuticCategoryId: generic.therapeuticCategory?.id,
      });
    }
  }, [generic]);

  const updateMutation = useMutation({
    mutationFn: (data: Partial<CreateDrugGenericData>) =>
      drugGenericService.update(id!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["drug-generics"] });
      queryClient.invalidateQueries({ queryKey: ["drug-generic", id] });
      navigate("/drug-generics");
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    updateMutation.mutate(formData);
  };

  if (isLoading) {
    return <div className="p-6">Loading...</div>;
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-800"
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </button>
      </div>

      <div className="max-w-2xl">
        <h1 className="text-2xl font-bold mb-6">Edit Drug Generic</h1>

        <form
          onSubmit={handleSubmit}
          className="bg-white rounded-lg shadow p-6 space-y-4"
        >
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Generic Name *
            </label>
            <input
              type="text"
              required
              value={formData.name || ""}
              onChange={(e) =>
                setFormData({ ...formData, name: e.target.value })
              }
              className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Code
            </label>
            <input
              type="text"
              value={formData.code || ""}
              onChange={(e) =>
                setFormData({ ...formData, code: e.target.value })
              }
              className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Schedule Type
            </label>
            <select
              value={formData.scheduleType || ""}
              onChange={(e) =>
                setFormData({ ...formData, scheduleType: e.target.value })
              }
              className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select schedule</option>
              {SCHEDULE_TYPES.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              value={formData.description || ""}
              onChange={(e) =>
                setFormData({ ...formData, description: e.target.value })
              }
              rows={3}
              className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="submit"
              disabled={updateMutation.isPending}
              className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              <Save className="h-4 w-4" />
              {updateMutation.isPending ? "Saving..." : "Update Generic"}
            </button>
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="px-4 py-2 border rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
          </div>

          {updateMutation.isError && (
            <div className="text-red-600 text-sm">
              Error updating generic. Please try again.
            </div>
          )}
        </form>
      </div>
    </div>
  );
}
